import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import MobileBottomNav from '../../components/feature/MobileBottomNav';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { categories } from '../../mocks/categories';
import { featuredProducts } from '../../mocks/products';

type Product = typeof featuredProducts[0];

const ALL_CATS = ['Tümü', ...categories.map((c) => c.name)];

export default function Kategoriler() {
  const [selectedCategory, setSelectedCategory] = useState<string>('Tümü');
  const [sortBy, setSortBy] = useState<string>('popular');
  const [addedIds, setAddedIds] = useState<number[]>([]);
  const [wishlist, setWishlist] = useState<number[]>([]);
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  const catScrollRef = useRef<HTMLDivElement>(null);

  const filteredProducts = featuredProducts.filter((p) => {
    return selectedCategory === 'Tümü' || p.category === selectedCategory;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === 'price-low') return a.price - b.price;
    if (sortBy === 'price-high') return b.price - a.price;
    return 0;
  });

  const addToCart = (e: React.MouseEvent, product: Product) => {
    e.preventDefault();
    e.stopPropagation();
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItem = cart.find((item: { id: number }) => item.id === product.id);
    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      cart.push({ ...product, quantity: 1 });
    }
    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    setAddedIds((prev) => [...prev, product.id]);
    setTimeout(() => setAddedIds((prev) => prev.filter((id) => id !== product.id)), 2000);
  };

  const toggleWishlist = (e: React.MouseEvent, id: number) => {
    e.preventDefault();
    e.stopPropagation();
    setWishlist((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const sortLabels: Record<string, string> = {
    popular: 'Popüler',
    'price-low': 'Önce Ucuz',
    'price-high': 'Önce Pahalı',
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      <Navbar />
      <WhatsAppButton />

      {/* Hero */}
      <section className="pt-28 pb-6 md:pb-10 bg-[#faf9f7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-orange-500 text-xs font-bold tracking-[0.3em] uppercase mb-2">El Yapımı Tasarımlar</p>
          <h1 className="text-3xl md:text-5xl font-black font-['Playfair_Display'] text-gray-900 mb-3">
            TÜM ÜRÜNLER
          </h1>
          <p className="text-sm text-gray-500 max-w-xl mx-auto">
            Özel günleriniz için el yapımı, kişiselleştirilebilir tasarımlar
          </p>
        </div>
      </section>

      {/* Desktop: Category Filter + Sort Bar */}
      <section className="hidden md:block sticky top-0 z-30 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 flex-1">
            {ALL_CATS.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap cursor-pointer transition-all duration-200 uppercase tracking-wide ${
                  selectedCategory === cat
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-xs text-gray-400 whitespace-nowrap font-medium">
              {sortedProducts.length} ürün
            </span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white cursor-pointer font-medium"
            >
              <option value="popular">Popüler</option>
              <option value="price-low">Önce Ucuz</option>
              <option value="price-high">Önce Pahalı</option>
            </select>
          </div>
        </div>
      </section>

      {/* Mobile: Mini sort bar */}
      <div className="md:hidden flex items-center justify-between px-4 py-2.5 bg-white border-b border-gray-100 sticky top-[56px] z-20">
        <span className="text-xs text-gray-500 font-medium">{sortedProducts.length} ürün</span>
        <div className="relative">
          <button
            onClick={() => setMobileSortOpen(!mobileSortOpen)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-xs font-semibold text-gray-700 cursor-pointer"
          >
            <i className="ri-sort-desc text-sm"></i>
            <span>{sortLabels[sortBy]}</span>
            <i className={`ri-arrow-down-s-line text-sm transition-transform ${mobileSortOpen ? 'rotate-180' : ''}`}></i>
          </button>
          {mobileSortOpen && (
            <div className="absolute right-0 top-full mt-1 bg-white rounded-xl border border-gray-100 overflow-hidden z-50 w-36" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
              {Object.entries(sortLabels).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => { setSortBy(key); setMobileSortOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 text-xs font-medium cursor-pointer transition-colors ${
                    sortBy === key ? 'bg-orange-50 text-orange-500' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Products Grid */}
      <section className="py-4 md:py-10 pb-44 md:pb-10">
        <div className="max-w-7xl mx-auto px-3 md:px-6 lg:px-8">

          {/* Mobile: 2 column app-style grid */}
          <div className="grid grid-cols-2 gap-3 md:hidden" data-product-shop>
            {sortedProducts.map((product) => (
              <Link
                to={`/urun/${product.slug}`}
                key={product.id}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-100 active:scale-95 transition-all duration-200 cursor-pointer block flex flex-col"
                style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}
              >
                <div className="relative bg-[#f7f5f2] overflow-hidden rounded-t-2xl" style={{ paddingBottom: '100%' }}>
                  <img
                    src={product.image}
                    alt={product.name}
                    className="absolute inset-0 w-full h-full object-contain p-2.5 group-active:scale-105 transition-transform duration-300"
                  />
                  <button
                    onClick={(e) => toggleWishlist(e, product.id)}
                    className={`absolute top-2 right-2 z-10 w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-all ${
                      wishlist.includes(product.id)
                        ? 'bg-red-50 text-red-500'
                        : 'bg-white/90 text-gray-400'
                    }`}
                  >
                    <i className={`${wishlist.includes(product.id) ? 'ri-heart-fill' : 'ri-heart-line'} text-sm`}></i>
                  </button>
                  {product.stock > 0 && product.stock <= 5 && (
                    <span className="absolute bottom-2 left-2 z-10 bg-yellow-400 text-gray-900 text-[9px] font-bold px-2 py-0.5 rounded-md">
                      Son {product.stock}!
                    </span>
                  )}
                </div>

                <div className="p-2.5 flex flex-col flex-1">
                  <h3 className="text-xs font-semibold text-gray-900 line-clamp-2 mb-1.5 leading-snug flex-1">
                    {product.name}
                  </h3>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-gray-900">
                      {product.price.toLocaleString('tr-TR')} ₺
                    </span>
                    {product.oldPrice > 0 && (
                      <span className="text-[10px] text-gray-400 line-through">
                        {product.oldPrice.toLocaleString('tr-TR')} ₺
                      </span>
                    )}
                  </div>
                  {product.reviewCount > 0 && (
                    <div className="flex items-center gap-0.5 mb-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className={`${i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'} text-yellow-400 text-[10px]`}></i>
                      ))}
                      <span className="text-[9px] text-gray-400 ml-0.5">({product.reviewCount})</span>
                    </div>
                  )}
                  <button
                    onClick={(e) => addToCart(e, product)}
                    className={`w-full py-2 text-[11px] font-bold rounded-xl transition-all flex items-center justify-center gap-1 whitespace-nowrap cursor-pointer ${
                      addedIds.includes(product.id)
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-900 text-white active:bg-gray-700'
                    }`}
                  >
                    <i className={`${addedIds.includes(product.id) ? 'ri-check-line' : 'ri-shopping-bag-line'} text-sm`}></i>
                    {addedIds.includes(product.id) ? 'Eklendi!' : 'Sepete Ekle'}
                  </button>
                </div>
              </Link>
            ))}
          </div>

          {/* Desktop: 3-4 col grid */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-product-shop>
            {sortedProducts.map((product) => (
              <Link
                to={`/urun/${product.slug}`}
                key={product.id}
                className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-gray-200 transition-all duration-300 cursor-pointer block flex flex-col"
              >
                <div className="relative bg-[#f7f5f2] overflow-hidden" style={{ paddingBottom: '110%' }}>
                  <img
                    src={product.image}
                    alt={product.name}
                    className="absolute inset-0 w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-500"
                  />
                  <button
                    onClick={(e) => toggleWishlist(e, product.id)}
                    className={`absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full transition-all cursor-pointer ${
                      wishlist.includes(product.id)
                        ? 'bg-red-50 text-red-500'
                        : 'bg-white/90 text-gray-400 hover:text-red-400'
                    }`}
                  >
                    <i className={wishlist.includes(product.id) ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                  </button>
                  {product.stock > 0 && product.stock <= 5 && (
                    <span className="absolute bottom-3 left-3 z-10 bg-yellow-400 text-gray-900 text-[11px] font-bold px-2.5 py-1 rounded-md">
                      Son {product.stock} adet!
                    </span>
                  )}
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-2 leading-snug flex-1">
                    {product.name}
                  </h3>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-lg font-bold text-gray-900">
                      {product.price.toLocaleString('tr-TR')} ₺
                    </span>
                    {product.oldPrice > 0 && (
                      <span className="text-sm text-gray-400 line-through">
                        {product.oldPrice.toLocaleString('tr-TR')} ₺
                      </span>
                    )}
                  </div>
                  {product.reviewCount > 0 && (
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className={`${i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'} text-yellow-400 text-xs`}></i>
                      ))}
                      <span className="text-xs text-gray-400">({product.reviewCount})</span>
                    </div>
                  )}
                  <button
                    onClick={(e) => addToCart(e, product)}
                    className={`w-full py-2.5 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer uppercase tracking-wide ${
                      addedIds.includes(product.id)
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                  >
                    <i className={addedIds.includes(product.id) ? 'ri-check-line' : 'ri-shopping-cart-line'}></i>
                    {addedIds.includes(product.id) ? 'Eklendi!' : 'Sepete Ekle'}
                  </button>
                </div>
              </Link>
            ))}
          </div>

          {sortedProducts.length === 0 && (
            <div className="text-center py-24">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-inbox-line text-5xl text-gray-300"></i>
              </div>
              <p className="text-gray-400 text-base">Bu kategoride ürün bulunamadı.</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
      <MobileBottomNav />

      {/* Mobile: Category Bottom Bar */}
      <div className="md:hidden fixed left-0 right-0 z-40 bg-white border-t border-gray-100" style={{ bottom: 'calc(64px + env(safe-area-inset-bottom, 0px))' }}>
        <div className="px-3 pt-2 pb-2">
          <div
            ref={catScrollRef}
            className="flex items-center gap-2 overflow-x-auto"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {ALL_CATS.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  selectedCategory === cat
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {cat === 'Tümü' ? (
                  <span className="flex items-center gap-1">
                    <i className="ri-apps-line text-xs"></i> Tümü
                  </span>
                ) : (
                  cat
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
